import { ReactNode, memo } from 'react';

import { Styles, css, sva } from '@flipster/styled-system/css';
import { isNullable } from '@flipster/utils/nullable';
import {
  BigNumber,
  GetNumberUnitOptions,
  NumberFormatterOptions,
  formatToShortNumber,
  numberFormatter,
} from '@flipster/utils/num';

import ArrowDownRightIcon from '../icon/generated/ArrowDownRightIcon';
import ArrowUpRightIcon from '../icon/generated/ArrowUpRightIcon';

const numberRecipe = sva({
  slots: ['control', 'number'],
  base: {
    control: {
      display: 'inline-flex',
      alignItems: 'center',
      whiteSpace: 'nowrap',
    },
  },
  variants: {
    color: {
      positive: {
        control: { color: 'content.state.positive' },
        number: { color: 'content.state.positive' },
      },
      negative: {
        control: { color: 'content.state.negative' },
        number: { color: 'content.state.negative' },
      },
    },
    sign: {
      plus: { number: { _before: { content: '"+"' } } },
      minus: { number: { _before: { content: '"-"' } } },
    },
    parentheses: {
      true: {
        control: {
          _before: { content: '"("' },
          _after: { content: '")"' },
        },
      },
    },
  },
});

type NumberType = BigNumber | string | number | undefined | null;

type Options = {
  abs?: boolean;
  withPlus?: boolean;
  withColor?: boolean;
  withArrow?: boolean;
  zeroString?: string;
} & NumberFormatterOptions;

interface RecipeResult {
  color?: 'positive' | 'negative';
  sign?: 'plus' | 'minus';
}

export const numberHelper = (
  num: NumberType,
  { method, withComma = true, toFixed, precision, withColor, withPlus, withArrow, abs, zeroString }: Options,
): [string, RecipeResult] => {
  if (isNullable(num)) return ['-', {}];
  const formatted = numberFormatter(num, { method, precision, toFixed, withComma }).replace('-', '');

  const isNotZero = Number(formatted) != 0;
  const isPositive = isNotZero && Number(num) > 0;
  const isNegative = isNotZero && Number(num) < 0;

  const recipeResult: RecipeResult = {};
  if (withColor) {
    if (isPositive) recipeResult.color = 'positive';
    if (isNegative) recipeResult.color = 'negative';
  }
  if (!withArrow) {
    if (!abs && isNegative) recipeResult.sign = 'minus';
    if (withPlus && isPositive) recipeResult.sign = 'plus';
  }

  const value = isNotZero ? formatted : zeroString ?? formatted;
  return [value, recipeResult];
};

interface Props {
  value: NumberType;
  prefix?: ReactNode;
  suffix?: ReactNode;
  className?: string;
}

const BaseNumber = memo(({ value, prefix, suffix, className }: Props) => {
  return (
    <span className={className} translate='no'>
      {prefix}
      {String(value)}
      {suffix}
    </span>
  );
});

export interface NumberProps extends Options {
  value: NumberType;
  prefix?: ReactNode;
  suffix?: ReactNode;
  css?: Styles;
}

/**
 * for more contexts, see links below:
 * - pos/neg color should be determined from **formatted** number
 *   - https://prextech.slack.com/archives/C02465XKMDZ/p1740017789402099
 * - the color of currency follows pos/neg color of formatted number by default
 *   - https://prextech.slack.com/archives/C073YCY3708/p1740031035990279
 */
const FormattedNumber = memo(({ value, prefix, css: cssProps, suffix, ...options }: NumberProps) => {
  const [showValue, recipeOptions] = numberHelper(value, options);

  const classes = numberRecipe.raw(recipeOptions);

  return (
    <BaseNumber
      className={css(cssProps, classes.number)}
      value={showValue}
      prefix={prefix}
      suffix={suffix && <>&nbsp;{suffix}</>}
    />
  );
});

type ShortNumberProps = Omit<NumberProps, 'method' | 'abs'> & GetNumberUnitOptions;

const FormattedShortNumber = memo(
  ({ value, prefix, suffix, minUnit, css: cssProps, withComma, toFixed, ...options }: ShortNumberProps) => {
    const [numberValue, recipeValue] = numberHelper(value, options);

    const classes = numberRecipe.raw(recipeValue);

    let showValue = numberValue;
    if (!!value && Number(value) !== 0) {
      showValue = formatToShortNumber(numberValue, {
        withComma,
        toFixed,
        minUnit,
      });
    }

    return (
      <BaseNumber
        className={css(cssProps, classes.number)}
        value={showValue}
        prefix={prefix}
        suffix={suffix && <>&nbsp;{suffix}</>}
      />
    );
  },
);

export interface PercentProps extends Omit<Options, 'abs'> {
  value: NumberType;
  suffix?: ReactNode;
  css?: Styles;
  isRate?: boolean;
  withParentheses?: boolean;
  withArrow?: boolean;
}

const FormattedPercent = memo(
  ({ value, suffix, isRate, withArrow, withParentheses, precision = 2, css: cssProps, ...options }: PercentProps) => {
    let numberValue: NumberType = value;
    if (!!value && isRate) numberValue = Number(value) * 100;

    const [showValue, recipeValue] = numberHelper(numberValue, { precision, withArrow, ...options });
    const recipeStyle = numberRecipe.raw({ ...recipeValue, parentheses: withParentheses });

    return (
      <span
        className={css(recipeStyle.control, { '& svg': { h: '0.9em', w: '0.9em', mr: 2 } }, cssProps)}
        translate='no'
      >
        {withArrow && recipeValue.color === 'positive' && <ArrowUpRightIcon />}
        {withArrow && recipeValue.color === 'negative' && <ArrowDownRightIcon />}
        <BaseNumber
          value={showValue}
          className={css(recipeStyle.number)}
          suffix={suffix ? <>%&nbsp;{suffix}</> : '%'}
        />
      </span>
    );
  },
);

const Formatted = {
  Number: FormattedNumber,
  ShortNumber: FormattedShortNumber,
  Percent: FormattedPercent,
};

export default Formatted;
